import { Flex, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconError from '@/assets/icons/icon-error.svg?react';
import Card from '@/components/RCard';
import useCurrency from '@/contexts/useCurrency';
import { DataProps } from '@/types/result';
import { converToMillion, formatCompactNumber } from '@/utils';

import DataCard from './MetricCard';

const KeyMetrics: React.FC<DataProps> = ({ data, isLoading, error }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { currencySymbol } = useCurrency();
  const cardData = [
    {
      title: t('result.summary.comparisonMetrics.averageSalesPrice.title'),
      withStorage:
        data?.result.averageSalesPrice.toFixed(2) + ` ${currencySymbol}/MWh`,
      withoutStorage:
        data?.result.averageSalesPriceWos.toFixed(2) + ` ${currencySymbol}/MWh`,
      toolTip: t('result.summary.comparisonMetrics.averageSalesPrice.tooltip'),
      toolTipDescription: t(
        'result.summary.comparisonMetrics.averageSalesPrice.description'
      ),
    },
    {
      title: t('result.summary.comparisonMetrics.ebitda.title'),
      withStorage: `${currencySymbol}${formatCompactNumber(data?.result.ebitda || 0)}`,
      withoutStorage: `${currencySymbol}${formatCompactNumber(data?.result.ebitdaWos || 0)}`,
      toolTip: t('result.summary.comparisonMetrics.ebitda.tooltip'),
      toolTipDescription: t(
        'result.summary.comparisonMetrics.ebitda.description'
      ),
    },
    {
      title: t('result.summary.comparisonMetrics.netPresentValue.title'),
      withStorage: `${currencySymbol}${formatCompactNumber(data?.result.npvOfFreeCashFlowToFirm || 0)}`,
      withoutStorage: `${currencySymbol}${formatCompactNumber(data?.result.npvOfFreeCashFlowToFirmOfStorage || 0)}`,
      toolTip: t('result.summary.comparisonMetrics.netPresentValue.tooltip'),
      toolTipDescription: t(
        'result.summary.comparisonMetrics.netPresentValue.description'
      ),
    },
    {
      title: t('result.summary.comparisonMetrics.soldEnergy.title'),
      withStorage: converToMillion(data?.result.totalSoldEnergy || 0) + ' MWh',
      withoutStorage: converToMillion(data?.result.soldEnergyWos || 0) + ' MWh',
      toolTip: t('result.summary.comparisonMetrics.soldEnergy.tooltip'),
      toolTipDescription: (
        <Flex vertical gap={12}>
          <Typography.Text>
            <strong>
              {t('result.summary.comparisonMetrics.soldEnergy.withStorage')}
            </strong>
          </Typography.Text>
          <Typography.Text>
            <strong>
              {t('result.summary.comparisonMetrics.soldEnergy.withoutStorage')}
            </strong>
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: t('result.summary.comparisonMetrics.levelizedCostOfEnergy.title'),
      withStorage:
        data?.result.levelizedCostOfEnergy?.toFixed(2) +
        ` ${currencySymbol}/MWh`,
      withoutStorage:
        data?.result.levelizedCostOfEnergyWos?.toFixed(2) +
        ` ${currencySymbol}/MWh`,
      toolTip: t(
        'result.summary.comparisonMetrics.levelizedCostOfEnergy.tooltip'
      ),
      toolTipDescription: t(
        'result.summary.comparisonMetrics.levelizedCostOfEnergy.description'
      ),
    },
    {
      title: t('result.summary.comparisonMetrics.batteryUsageCostPerMwh.title'),
      withStorage:
        data?.result.batteryUsageCostPerMwh.toFixed(2) +
        ` ${currencySymbol}/MWh`,
      withoutStorage: '-',
      toolTip: t(
        'result.summary.comparisonMetrics.batteryUsageCostPerMwh.tooltip'
      ),
      toolTipDescription: t(
        'result.summary.comparisonMetrics.batteryUsageCostPerMwh.description'
      ),
    },
  ];
  return (
    <Card
      title={t('result.summary.comparisonMetrics.title')}
      $paddingBody="16px 24px"
      style={{ marginTop: '24px' }}
    >
      {isLoading ? (
        <Flex justify="center" align="center" style={{ height: 250 }}>
          <Spin style={{ margin: '20px' }} />
        </Flex>
      ) : error ? (
        <Flex
          vertical
          gap={12}
          justify="center"
          align="center"
          style={{ height: 250 }}
        >
          <IconError width={50} height={50} fill={theme.colors.tagFailure} />
          <Typography.Text className="fs-14-regular text-gray-color">
            {t('result.error.fetchDataError')}
          </Typography.Text>
        </Flex>
      ) : (
        <Flex gap={24} wrap="wrap">
          {cardData.map((data, index) => (
            <DataCard
              key={index}
              title={data.title}
              withStorage={data.withStorage}
              withoutStorage={data.withoutStorage}
              toolTip={data.toolTip}
              toolTipDescription={data.toolTipDescription}
            />
          ))}
        </Flex>
      )}
    </Card>
  );
};

export default KeyMetrics;
